import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import createFakeEvent from '../../lib/createFakeEvent';
import Icon from '../Icon/Icon';

const TOOLTIP_MAX_WIDTH = 128;

export const calculateSliderPosition = (pos, clientWidth) => {
  let newPos = pos;
  if (newPos > clientWidth) {
    newPos = clientWidth;
  } else if (newPos < 0) {
    newPos = 0;
  }
  return newPos;
};

export const calculateSliderPositionWithValue = (value, clientWidth) => calculateSliderPosition(clientWidth * value, clientWidth);

export const setSliderHandlePosition = (pos, clientWidth, min, max) => {
  let value = calculateSliderPositionWithValue(pos, clientWidth);
  const range = max - min;
  if (min !== 0 || max !== 1) {
    value = (pos - min) / range;
    value = value * clientWidth;
  }
  return value;
};

/**
 * `Slider` is a component for selecting values within a range.
 *
 * @property {number} max The maximum value of the range. The user will not be able to select values higher than this value.
 * Defaults to 1.
 * @property {number} min The minimum value of the range. The user will not be able to select values lower than this value.
 * Defaults to 0.
 * @property {number} value The slider value. Must be within the range. Defaults to 0.
 */
export default class Slider extends PureComponent {

  static displayName = 'Slider';

  static propTypes = {
    animate: PropTypes.bool,
    children: PropTypes.node,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    indexPoints: PropTypes.arrayOf(PropTypes.object),
    label: PropTypes.node,
    min: PropTypes.number,
    max: PropTypes.number,
    onChange: PropTypes.func,
    onHoverChange: PropTypes.func,
    onIndexPointClick: PropTypes.func,
    onIndexPointChange: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    scale: PropTypes.number,
    showSliderHandle: PropTypes.bool,
    showHoveredArea: PropTypes.bool,
    value: PropTypes.number
  };

  static defaultProps = {
    animate: true,
    indexPoints: [],
    min: 0,
    max: 1,
    scale: 1,
    showSliderHandle: true,
    showHoveredArea: false
  };

  constructor(props) {
    super(props);
    this.state = {
      indexPoints: props.indexPoints,
    };
    this.slide = this.slide.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.animateHovered = this.animateHovered.bind(this);
    this.animateTooltip = this.animateTooltip.bind(this);
  }

  componentDidMount() {
    this.setNewSliderHandlePosition(this.props.value);
  }

  componentDidUpdate(prevProps) {
    const { value, indexPoints } = this.props;
    if (typeof value === 'number' && prevProps.value !== value) this.setNewSliderHandlePosition(value);
    if (typeof indexPoints === typeof Slider.defaultProps.indexPoints && JSON.stringify(prevProps.indexPoints) !== JSON.stringify(indexPoints)) {
      this.setState({ indexPoints: indexPoints });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('mousemove', this.slide);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('mousemove', this.animateHovered);
    window.removeEventListener('mousemove', this.animateTooltip);
  }

  onIndexPointClick = (id, e) => {
    if (this.props.onIndexPointClick && !this.props.disabled) {
      this.props.onIndexPointClick(createFakeEvent(e.target, undefined, this.sliderWrapper.dataset), id);
    }
  };

  onMouseUp(e) {
    if (!this.props.disabled) {
      window.removeEventListener('mousemove', this.slide);
      window.removeEventListener('mouseup', this.onMouseUp);
    }
  };

  onMouseDown = e => {
    if (!this.props.disabled) {
      e.preventDefault();
      let newPos = e.pageX - this.sliderWrapper.getBoundingClientRect().left;
      newPos = this.calculatePosition(newPos);
      this.setState(
        {
          sliderHandleLeftStyle: newPos,
        },
        () => this.emitChange()
      );
      window.addEventListener('mousemove', this.slide, false);
      window.addEventListener('mouseup', this.onMouseUp, false);
    }
  };

  onIndexPointMouseDown = (idx, e) => {
    if (!this.props.disabled) {
      this.setState(
        {
          selectedIndexPointId: idx
        },
        () => {
          window.addEventListener('mousemove', this.slideIndex, false);
          window.addEventListener('mouseup', this.onIndexPointMouseUp, false);
        }
      );
    }
  };

  onIndexPointMouseUp = e => {
    if (!this.props.disabled) {
      this.setState(
        {
          selectedIndexPointId: null
        },
        () => {
          window.removeEventListener('mousemove', this.slideIndex);
          window.removeEventListener('mouseup', this.onIndexPointMouseUp);
        }
      );
    }
  };

  onMouseLeave = () => !this.props.disabled && this.props.onMouseLeave && this.props.onMouseLeave();

  onMouseEnter = () => !this.props.disabled && this.props.onMouseEnter && this.props.onMouseEnter();

  onHoverEnter = e => {
    if (!this.props.disabled) {
      e.preventDefault();
      let newPos = e.pageX - this.sliderWrapper.getBoundingClientRect().left;
      newPos = this.calculatePosition(newPos);
      this.setState(
        {
          hoveredLeftStyle: newPos,
          showTooltip: true
        },
        () => this.emitChange('hover')
      );
      window.addEventListener('mousemove', this.animateHovered, false);
      window.addEventListener('mousemove', this.animateTooltip, false);
    }
  };

  onHoverLeave = () => {
    if (!this.props.disabled) {
      this.setState({
        hoveredLeftStyle: 0,
        showTooltip: false
      });
    }
    window.removeEventListener('mousemove', this.animateHovered);
    window.removeEventListener('mousemove', this.animateTooltip);
  };

  getIndexPoints = () => {
    if (!this.sliderWrapper) {
      return [];
    }
    const { scale } = this.props;
    const { indexPoints } = this.state;
    const skipIndexes = [];
    return indexPoints
      .map((point, idx) => {
        if (point.position >= 0 && skipIndexes.indexOf(idx) === -1) {
          const leftPos = this.calculatePositionWithValue(point.position);
          const connectedIndex =
            point.connectedTo &&
            indexPoints.findIndex(i => point.connectedTo === i.name);
          if (connectedIndex && connectedIndex > -1) {
            const connectedPoint = indexPoints[connectedIndex];
            skipIndexes.push(connectedIndex);
            if (connectedPoint.position >= 0) {
              const connectedPointLeftPos = this.calculatePositionWithValue(
                connectedPoint.position
              );
              let pointStyle = {
                left: '100%',
                transform: `translateX(-50%) scaleX(${scale})`
              };
              let connectedPointStyle = {
                transform: `translateX(-50%) scaleX(${scale})`
              };
              if (connectedPointLeftPos > leftPos) {
                connectedPointStyle = {
                  left: '100%',
                  transform: `translateX(-50%) scaleX(${scale})`
                };
                pointStyle = {
                  transform: `translateX(-50%) scaleX(${scale})`
                };
              }
              return (
                <div key={idx}
                  className="core-slider-connected-index-points-wrapper"
                  style={{
                    left: `${connectedPointLeftPos < leftPos
                      ? connectedPointLeftPos
                      : leftPos}px`,
                    width: `${leftPos < connectedPointLeftPos
                      ? connectedPointLeftPos - leftPos
                      : leftPos - connectedPointLeftPos}px`
                  }}
                >
                  <div className="core-slider-connected-index-points-line" />
                  <Icon
                    key={`index_point_${connectedIndex}`}
                    className="core-slider-index-point"
                    onMouseDown={this.onIndexPointMouseDown.bind(
                      this,
                      connectedIndex
                    )}
                    onMouseUp={this.onIndexPointMouseUp.bind(
                      this,
                      connectedIndex
                    )}
                    name={point.iconName}
                    style={connectedPointStyle}
                  />
                  <Icon
                    key={`index_point_${idx}`}
                    className="core-slider-index-point"
                    onMouseDown={this.onIndexPointMouseDown.bind(this, idx)}
                    onMouseUp={this.onIndexPointMouseUp.bind(this, idx)}
                    name={point.iconName}
                    style={pointStyle}
                  />
                </div>
              );
            }
          }
          return point.iconName ? (
            <Icon
              key={`index_point_${idx}`}
              className="core-slider-index-point"
              onMouseDown={this.onIndexPointMouseDown.bind(this, idx)}
              onMouseUp={this.onIndexPointMouseUp.bind(this, idx)}
              name={point.iconName}
              style={{
                transform: `translateX(-50%) scaleX(${scale})`,
                left: `${leftPos}px`
              }}
            />
          ) : (
            <div
              key={`index_point_${idx}`}
              className="core-slider-index-point"
              style={{ left: `${leftPos}px` }}
              onClick={this.onIndexPointClick.bind(this, point.id || idx)}
            />
          );
        }
        return null;
      })
      .filter(p => p !== null);
  };

  getTooltip() {
    let pos = this.state.tooltipX;
    const { scale } = this.props;
    if (!this.props.children || !this.state.showTooltip) {
      return null;
    }
    if (this.sliderWrapper) {
      const slider = this.sliderWrapper.getBoundingClientRect();
      if (pos + TOOLTIP_MAX_WIDTH / 2 > slider.width) {
        pos = slider.width - TOOLTIP_MAX_WIDTH / 2;
      } else if (pos - TOOLTIP_MAX_WIDTH / 2 < 0) {
        pos = TOOLTIP_MAX_WIDTH / 2;
      }
    }
    return (
      <div
        className="core-slider-tooltip"
        style={{
          left: pos,
          bottom: `calc(100% + ${4 * scale}px`
        }}
      >
        {this.props.children}
      </div>
    );
  }

  animateHovered(e) {
    let newPos = e.pageX - this.sliderWrapper.getBoundingClientRect().left;
    newPos = this.calculatePosition(newPos);
    this.setState(
      {
        hoveredLeftStyle: newPos
      },
      () => this.emitChange('hover')
    );
  };

  animateTooltip(e) {
    const newX = e.pageX - this.sliderWrapper.getBoundingClientRect().left;

    this.setState({
      tooltipX: newX,
    });
  };

  calculatePositionWithValue = value => calculateSliderPositionWithValue(value, this.sliderWrapper.clientWidth);

  calculatePosition = pos => calculateSliderPosition(pos, this.sliderWrapper.clientWidth);

  setNewSliderHandlePosition = (pos) => {
    const { min, max } = this.props;
    const value = setSliderHandlePosition(pos, this.sliderWrapper.clientWidth, min, max);
    this.setState({
      sliderHandleLeftStyle: value,
    });
  };

  calculateValue = (val, fallback) => {
    try {
      return val / this.sliderWrapper.clientWidth;
    } catch (e) {
      return fallback;
    }
  };

  emitChange(change, data) {
    const { min, max, onChange, onHoverChange, onIndexPointChange, value } = this.props;
    const dataset = this.sliderWrapper.dataset;

    switch (change) {
      case 'hover':
        if (onHoverChange) {
          const value = this.calculateValue(this.state.hoveredLeftStyle, 0);

          onHoverChange(createFakeEvent(value, undefined, dataset));
        }
        break;
      case 'indexPoint':
        if (onIndexPointChange) {
          onIndexPointChange(createFakeEvent(data, undefined, dataset));
        }
        break;
      default:
        if (onChange) {
          let val = this.calculateValue(this.state.sliderHandleLeftStyle, value);

          if (min !== 0 || max !== 1) {
            const range = max - min;

            val = val * range + min;
          }
          onChange(createFakeEvent(val, undefined, dataset));
        }
        break;
    }
  };

  sliderWrapperRef = c => (this.sliderWrapper = c);

  slide(e) {
    let newPos = e.pageX - this.sliderWrapper.getBoundingClientRect().left;
    newPos = this.calculatePosition(newPos);
    this.setState(
      {
        sliderHandleLeftStyle: newPos
      },
      () => this.emitChange()
    );
  };

  slideIndex = e => {
    e.preventDefault();
    e.stopPropagation();
    if (this.state.selectedIndexPointId !== null) {
      const newPos = e.pageX - this.sliderWrapper.getBoundingClientRect().left;
      const newValue = this.calculateValue(this.calculatePosition(newPos));
      let changedIndexPoint;
      const indexPoints = this.state.indexPoints.map((i, idx) => {
        if (this.state.selectedIndexPointId === idx) {
          changedIndexPoint = { ...i, position: newValue };
          return changedIndexPoint;
        }
        return i;
      });
      this.setState({ indexPoints }, () =>
        this.emitChange('indexPoint', {
          indexPoint: changedIndexPoint,
          arrayIndex: this.state.selectedIndexPointId
        })
      );
    }
  };

  render() {
    const { animate, className, disabled, id, indexPoints, label, min, max, onHoverChange, onIndexPointChange, scale = 1,
      showSliderHandle, showHoveredArea, ...rest } = this.props;
    const hideClass = showSliderHandle ? 'show' : 'hide';
    const sliderHandleClass = `core-slider-handle ${hideClass} ${animate ? 'animate' : ''}`;
    const hoveredStyle = showHoveredArea ? { width: `${this.state.hoveredLeftStyle}px` } : { visibility: 'hidden' };
    const sliderId = id || (label && label.toLowerCase().replace(/ /, '-'));

    return (
      <div
        ref={this.sliderWrapperRef}
        className={cx('core-slider', className, { disabled })}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        data-testid="slider-wrapper"
        {...rest}
      >
        {label && <label id={sliderId} htmlFor="test">{label}</label>}
        {this.getTooltip()}
        <div className="core-slider-wrapper">
          <div
            id={sliderId}
            data-testid="core-slider-padding"
            className="core-slider-padding"
            onMouseDown={this.onMouseDown}
            onMouseLeave={showHoveredArea ? this.onHoverLeave : null}
            onMouseEnter={showHoveredArea ? this.onHoverEnter : null}
            style={{ transform: `scaleY(${scale}` }}
          />
          <div className={`core-slider-line ${animate ? 'animate' : ''}`} style={{ transform: `scaleY(${scale}` }}>
            <div
              className="core-slider-uncovered"
              onMouseDown={this.onMouseDown}
              onMouseEnter={showHoveredArea ? this.onHoverEnter : null}
            />
            <div className="core-slider-hovered" style={hoveredStyle} />
            <div
              className="core-slider-covered"
              style={{ width: `${this.state.sliderHandleLeftStyle}px` }}
              onMouseDown={this.onMouseDown}
              onMouseLeave={showHoveredArea ? this.onHoverLeave : null}
              onMouseEnter={showHoveredArea ? this.onHoverEnter : null}
            />
            {this.getIndexPoints()}
          </div>
          <div
            className="core-slider-handle-container"
            style={{
              transform: `translateX(${this.state.sliderHandleLeftStyle ||
              0}px) translateY(-50%) scale(${scale}`
            }}
          >
            <div
              className={sliderHandleClass}
              onMouseDown={this.onMouseDown}
              ref={c => {this.sliderHandle = c;}}
              onClick={this.onClickHandle}
            />
          </div>
        </div>
      </div>
    );
  }

}
